import React, { useEffect, useMemo, useState } from 'react';
import {
    Alert,
    Box,
    Breadcrumbs,
    Button,
    Link,
    MenuItem,
    Paper,
    Snackbar,
    Slider,
    Tab,
    Tabs,
    TextField,
    Typography,
} from '@mui/material';
import { Link as RouterLink, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import adminAgentService from '../services/AdminAgentService';
import McpServersEditor from './McpServersEditor';
import kbaseService from '../services/KbaseService';
import {
    inferVendorFromModel,
    getReasoningEffortLevels,
    supportsReasoningEffort,
    LLM_MODELS_BY_VENDOR,
    LLM_VENDORS,
    normalizeLlmVendor,
} from '../services/LlmCatalogService';

function normalizeString(value) {
    return String(value ?? '').trim();
}

function normalizeNumber(value) {
    if (value === null || value === undefined || value === '') return '';
    const n = Number(value);
    return Number.isFinite(n) ? n : '';
}

function normalizeTemperature(value) {
    const n = normalizeNumber(value);
    if (n === '') return '';
    if (n < 0) return 0;
    if (n > 1) return 1;
    return n;
}

function getAgentKbaseId(agent) {
    return normalizeString(
        agent?.kbaseId
        ?? agent?.kbase?.kbaseId
        ?? agent?.kbase?.id
        ?? agent?.knowledgeBaseId
        ?? agent?.knowledge_base_id
    );
}

function AdminAgentForm() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();
    const { clientId, agentId } = useParams();

    const isEdit = Boolean(agentId);

    const initialFromState = useMemo(() => {
        const state = location.state;
        if (state && state.agent && typeof state.agent === 'object') return state.agent;
        return null;
    }, [location.state]);

    const [kbases, setKbases] = useState([]);
    const [kbasesLoading, setKbasesLoading] = useState(false);

    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);

    const [agentName, setAgentName] = useState('');
    const [kbaseId, setKbaseId] = useState('');
    const [llmVendor, setLlmVendor] = useState('');
    const [llmModel, setLlmModel] = useState('');
    const [reasoningEffort, setReasoningEffort] = useState('');
    const hasReasoningEffort = supportsReasoningEffort(llmVendor, llmModel);
    const effortLevels = getReasoningEffortLevels(llmVendor, llmModel);
    const selectedEffort = effortLevels.includes(reasoningEffort) ? reasoningEffort : '';
    const [prompt, setPrompt] = useState('');
    const [supportTicketPrompt, setSupportTicketPrompt] = useState('');
    const [temperature, setTemperature] = useState('');
    const [maxTokens, setMaxTokens] = useState('');
    const [mcpServers, setMcpServers] = useState([]);
    const [activeTab, setActiveTab] = useState(0);

    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

    const modelOptions = useMemo(() => {
        const vendor = normalizeString(llmVendor);
        return LLM_MODELS_BY_VENDOR[vendor] || [];
    }, [llmVendor]);

    const hydrate = (a) => {
        setAgentName(normalizeString(a?.agentName));
        setKbaseId(getAgentKbaseId(a));
        const rawVendor = a?.llmVendor ?? a?.vendor ?? a?.llm_vendor ?? a?.provider ?? a?.llmProvider;
        const rawModel = a?.llmModel ?? a?.model ?? a?.llm_model;
        const model = normalizeString(rawModel);
        const vendor = (() => {
            const normalized = normalizeLlmVendor(rawVendor);
            if (normalized) return normalized;
            if (model) return inferVendorFromModel(model);
            return '';
        })();

        setLlmVendor(normalizeString(vendor));
        setLlmModel(model);
        setReasoningEffort(normalizeString(a?.reasoningEffort));
        setPrompt(normalizeString(a?.prompt));
        setSupportTicketPrompt(normalizeString(a?.supportTicketPrompt));
        setMcpServers(Array.isArray(a?.mcpServers) ? a.mcpServers : []);

        const temp = a?.temperature;
        setTemperature(temp === 0 ? 0 : normalizeNumber(temp));

        const existingMaxTokens = a?.maxTokens ?? a?.maxToken;
        const max = existingMaxTokens === 0 ? 0 : normalizeNumber(existingMaxTokens);
        setMaxTokens(isEdit ? max : (max === '' ? 1000 : max));
    };

    useEffect(() => {
        if (isEdit) return;
        const n = normalizeNumber(maxTokens);
        if (n === '') setMaxTokens(1000);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isEdit]);
    const temperatureNumber = normalizeNumber(temperature);
    const temperatureError = !hasReasoningEffort && temperatureNumber !== '' && (temperatureNumber < 0 || temperatureNumber > 1);
    const temperatureSliderValue = temperatureNumber === '' ? 0 : temperatureNumber;

    const maxTokensNumber = normalizeNumber(maxTokens);
    const maxTokensError = maxTokensNumber !== '' && maxTokensNumber <= 0;

    const canSave = Boolean(normalizeString(agentName))
        && Boolean(normalizeString(kbaseId))
        && !temperatureError
        && !maxTokensError;

    useEffect(() => {
        if (!clientId) return;
        const loadKbases = async () => {
            try {
                setKbasesLoading(true);
                const data = await kbaseService.getKbases(clientId);
                setKbases(Array.isArray(data) ? data : []);
            } catch (e) {
                console.error('Error loading kbases for admin agent form', e);
                setKbases([]);
            } finally {
                setKbasesLoading(false);
            }
        };
        loadKbases();
    }, [clientId]);

    useEffect(() => {
        if (!clientId) return;

        if (!isEdit) {
            hydrate(initialFromState || {});
            return;
        }

        const hasInitial = Boolean(initialFromState);
        if (hasInitial) {
            hydrate(initialFromState);
        }

        const load = async () => {
            try {
                setLoading(true);
                const data = await adminAgentService.getAgent(clientId, agentId);
                const agent = data && typeof data === 'object' && data.agent && typeof data.agent === 'object'
                    ? data.agent
                    : data;
                hydrate(agent || {});
            } catch (e) {
                console.error('Error loading admin agent', e);
                if (!hasInitial) {
                    setSnackbar({ open: true, message: t('adminAgents.loadError', 'Failed to load agent'), severity: 'error' });
                }
            } finally {
                setLoading(false);
            }
        };

        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [clientId, agentId]);

    useEffect(() => {
        if (kbasesLoading) return;
        if (isEdit) return;

        // Default KBase if backend value is null/empty.
        const current = normalizeString(kbaseId);
        if (current) return;
        const first = (Array.isArray(kbases) ? kbases : [])[0]?.kbaseId;
        if (first) setKbaseId(String(first));
    }, [kbases, kbaseId, kbasesLoading, isEdit]);

    useEffect(() => {
        // Default vendor/model if backend value is null/empty.
        const vendor = normalizeString(llmVendor);
        const nextVendor = vendor || LLM_VENDORS[0]?.value || '';
        if (nextVendor && nextVendor !== llmVendor) {
            setLlmVendor(nextVendor);
            return;
        }

        const model = normalizeString(llmModel);
        const opts = LLM_MODELS_BY_VENDOR[nextVendor] || [];
        const firstModel = opts[0]?.value || '';
        const modelIsValid = model && opts.some((m) => m.value === model);
        if (!modelIsValid && firstModel && firstModel !== llmModel) {
            setLlmModel(firstModel);
        }
    }, [llmVendor, llmModel]);

    const title = isEdit
        ? `${t('adminAgents.editTitle', 'Edit Agent')}: ${normalizeString(agentId)}`
        : t('adminAgents.newTitle', 'New Agent');

    const handleSave = async () => {
        if (!clientId) return;

        const promptValue = normalizeString(prompt);
        const supportTicketPromptValue = normalizeString(supportTicketPrompt);
        const payload = {
            agentName: normalizeString(agentName),
            kbaseId: normalizeString(kbaseId),
            llmVendor: normalizeString(llmVendor),
            llmModel: normalizeString(llmModel),
            reasoningEffort: selectedEffort || null,
            prompt: isEdit ? promptValue : undefined,
            supportTicketPrompt: isEdit ? supportTicketPromptValue : undefined,
            temperature: (() => {
                const n = normalizeTemperature(temperature);
                return n === '' ? undefined : n;
            })(),
            maxTokens: (() => {
                const n = normalizeNumber(maxTokens);
                return n === '' ? undefined : Math.trunc(n);
            })(),
            mcpServers,
        };

        if (!isEdit && !promptValue) delete payload.prompt;
        if (!isEdit && supportTicketPromptValue) payload.supportTicketPrompt = supportTicketPromptValue;

        Object.keys(payload).forEach((k) => {
            if (isEdit && k === 'prompt') return;
            if (isEdit && k === 'supportTicketPrompt') return;
            if (payload[k] === '') delete payload[k];
        });

        Object.keys(payload).forEach((k) => payload[k] === undefined && delete payload[k]);

        try {
            setSaving(true);
            if (isEdit) {
                await adminAgentService.updateAgent(clientId, agentId, payload);
                setSnackbar({ open: true, message: t('adminAgents.saved', 'Agent saved'), severity: 'success' });
            } else {
                const created = await adminAgentService.createAgent(clientId, payload);
                const createdId = normalizeString(created?.agentId);
                setSnackbar({ open: true, message: t('adminAgents.created', 'Agent created'), severity: 'success' });
                if (createdId) {
                    navigate(`/admin/clients/${encodeURIComponent(clientId)}/agents/${encodeURIComponent(createdId)}`, { replace: true, state: { agent: created } });
                } else {
                    navigate(`/admin/clients/${encodeURIComponent(clientId)}/agents`, { replace: true });
                }
                return;
            }
        } catch (e) {
            console.error('Error saving admin agent', e);
            setSnackbar({ open: true, message: t('adminAgents.saveError', 'Failed to save agent'), severity: 'error' });
            return;
        } finally {
            setSaving(false);
        }

        navigate(`/admin/clients/${encodeURIComponent(clientId)}/agents`, { replace: true });
    };

    const handleDiscoverMcpTools = async (serverId, nextServers) => {
        await adminAgentService.updateAgent(clientId, agentId, { mcpServers: nextServers });
        return adminAgentService.discoverMcpTools(clientId, agentId, serverId);
    };

    const handleDeleteMcpServer = async (serverId) => {
        await adminAgentService.deleteMcpServer(clientId, agentId, serverId);
    };

    return (
        <Box sx={{ p: 2 }}>
            <Box sx={{ mb: 2 }}>
                <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 0.5 }}>
                    <Link component={RouterLink} underline="hover" color="inherit" to="/customer-list" sx={{ fontSize: 13 }}>
                        {t('adminAgents.clientList', 'Client List')}
                    </Link>
                    <Link
                        component={RouterLink}
                        underline="hover"
                        color="inherit"
                        to={`/admin/clients/${encodeURIComponent(clientId)}/agents`}
                        sx={{ fontSize: 13 }}
                    >
                        {t('adminAgents.breadcrumbAgents', 'Bot Agents')}
                    </Link>
                    <Typography color="text.primary" sx={{ fontSize: 13 }}>
                        {isEdit ? t('adminAgents.breadcrumbEdit', 'Edit') : t('adminAgents.breadcrumbNew', 'New')}
                    </Typography>
                </Breadcrumbs>
                <Typography variant="h5" fontWeight="bold">{title}</Typography>
                <Typography variant="body2" color="text.secondary">
                    {clientId ? `Client: ${clientId}` : ''}
                </Typography>
            </Box>

            <Paper elevation={0} sx={{ p: 2, borderRadius: 2 }}>
                <Tabs
                    value={activeTab}
                    onChange={(event, value) => setActiveTab(value)}
                    sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
                >
                    <Tab label={t('agents.settingsTab', 'Agent Settings')} />
                    <Tab label={t('mcp.title', 'MCP Servers')} />
                </Tabs>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, width: '100%', maxWidth: activeTab === 0 ? 880 : 'none' }}>
                    <Box sx={{ display: activeTab === 0 ? 'contents' : 'none' }}>
                    <TextField
                        label={t('adminAgents.agentName', 'Agent Name')}
                        value={agentName}
                        onChange={(e) => setAgentName(e.target.value)}
                        required
                        fullWidth
                        disabled={loading || saving}
                    />

                    <TextField
                        select
                        label={t('adminAgents.kbase', 'KBase')}
                        value={kbaseId}
                        onChange={(e) => setKbaseId(e.target.value)}
                        required
                        fullWidth
                        disabled={loading || saving}
                        helperText={kbasesLoading ? t('adminAgents.loadingKbases', 'Loading kbases…') : (kbases?.length ? '' : t('adminAgents.noKbases', 'No kbases found'))}
                    >
                        {(() => {
                            const current = normalizeString(kbaseId);
                            const exists = (Array.isArray(kbases) ? kbases : []).some((k) => normalizeString(k?.kbaseId) === current);
                            if (current && !exists) {
                                return <MenuItem value={current}>{`Current: ${current}`}</MenuItem>;
                            }
                            return null;
                        })()}
                        {(Array.isArray(kbases) ? kbases : []).map((k) => (
                            <MenuItem key={String(k?.kbaseId)} value={String(k?.kbaseId || '')}>
                                {k?.kbaseName ? String(k.kbaseName) : String(k?.kbaseId || '')}
                            </MenuItem>
                        ))}
                    </TextField>

                    <Box sx={{
                        display: 'grid',
                        gridTemplateColumns: { xs: 'minmax(0, 1fr)', sm: 'minmax(0, 1fr) minmax(0, 1.6fr) minmax(0, 1fr)' },
                        gap: 2,
                        alignItems: 'start',
                    }}>
                        <TextField
                            select
                            label={t('adminAgents.vendor', 'LLM Vendor')}
                            value={llmVendor}
                            onChange={(e) => {
                                const nextVendor = e.target.value;
                                setLlmVendor(nextVendor);
                                const opts = LLM_MODELS_BY_VENDOR[String(nextVendor)] || [];
                                setLlmModel(opts[0]?.value || '');
                            }}
                            fullWidth
                            disabled={loading || saving}
                        >
                            {LLM_VENDORS.map((v) => (
                                <MenuItem key={v.value} value={v.value}>{v.label}</MenuItem>
                            ))}
                        </TextField>

                        <TextField
                            select
                            label={t('adminAgents.model', 'LLM Model')}
                            value={llmModel}
                            onChange={(e) => setLlmModel(e.target.value)}
                            fullWidth
                            disabled={loading || saving || !normalizeString(llmVendor)}
                            helperText={!normalizeString(llmVendor) ? t('adminAgents.pickVendor', 'Select a vendor first') : ''}
                        >
                            {(() => {
                                const current = normalizeString(llmModel);
                                const exists = modelOptions.some((m) => m.value === current);
                                if (current && !exists) {
                                    return <MenuItem value={current}>{`Current: ${current}`}</MenuItem>;
                                }
                                return null;
                            })()}
                            {modelOptions.map((m) => (
                                <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>
                            ))}
                        </TextField>

                        {hasReasoningEffort && (
                            <TextField select fullWidth
                                label={t('agents.reasoningEffort', 'Reasoning effort')}
                                value={selectedEffort}
                                onChange={(e) => setReasoningEffort(e.target.value)}
                                disabled={loading || saving}
                            >
                                <MenuItem value="">{t('agents.modelDefault', 'Model default')}</MenuItem>
                                {effortLevels.map((level) => (
                                    <MenuItem key={level} value={level}>{t(`agents.effortLevels.${level}`, level)}</MenuItem>
                                ))}
                            </TextField>
                        )}
                        {hasReasoningEffort && (
                            <Typography variant="caption" color="text.secondary" sx={{ gridColumn: '1 / -1', px: 1.75, mt: -1 }}>
                                {t(llmVendor === 'Bedrock' ? 'agents.bedrockClaudeEffortHelp' : 'agents.reasoningEffortHelp')}
                            </Typography>
                        )}
                    </Box>
                    <TextField
                        label={t('adminAgents.prompt', 'Prompt')}
                        value={prompt}
                        onChange={(e) => setPrompt(e.target.value)}
                        fullWidth
                        disabled={loading || saving}
                        multiline
                        minRows={5}
                    />

                    <TextField
                        label={t('adminAgents.supportTicketPrompt', 'Support Ticket Prompt')}
                        value={supportTicketPrompt}
                        onChange={(e) => setSupportTicketPrompt(e.target.value)}
                        fullWidth
                        disabled={loading || saving}
                        multiline
                        minRows={4}
                        helperText={t(
                            'adminAgents.supportTicketPromptHelp',
                            'Controls when this agent offers or opens support tickets.'
                        )}
                    />

                    {!hasReasoningEffort && (<Box
                        sx={{
                            border: 1,
                            borderColor: temperatureError ? 'error.main' : 'divider',
                            borderRadius: 1,
                            px: 2,
                            py: 0.75,
                        }}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Typography variant="body2" color={temperatureError ? 'error.main' : 'text.secondary'}>
                                {t('adminAgents.temperature', 'Temperature (0-1)')}
                            </Typography>
                            <Slider
                                size="small"
                                value={temperatureSliderValue}
                                onChange={(event, value) => setTemperature(Array.isArray(value) ? value[0] : value)}
                                min={0}
                                max={1}
                                step={0.1}
                                valueLabelDisplay="auto"
                                disabled={loading || saving}
                                aria-label={t('adminAgents.temperature', 'Temperature (0-1)')}
                                sx={{ flex: 1 }}
                            />
                            <Typography variant="body2" color="text.primary">
                                {temperatureSliderValue.toFixed(1)}
                            </Typography>
                        </Box>
                        {temperatureError ? (
                            <Typography variant="caption" color="error">
                                {t('adminAgents.temperatureError', 'Temperature must be between 0 and 1')}
                            </Typography>
                        ) : null}
                    </Box>)}

                    <TextField
                        label={t('adminAgents.maxTokens', 'Max Tokens')}
                        value={maxTokens}
                        onChange={(e) => setMaxTokens(e.target.value)}
                        fullWidth
                        disabled={loading || saving}
                        type="number"
                        inputProps={{ min: 1, step: 1 }}
                        error={maxTokensError}
                        helperText={maxTokensError ? t('adminAgents.maxTokensError', 'Max tokens must be greater than 0') : (!isEdit ? t('adminAgents.maxTokensDefault', 'Default: 1000') : '')}
                    />
                    </Box>

                    {activeTab === 1 ? (
                        <McpServersEditor
                            servers={mcpServers}
                            onChange={setMcpServers}
                            onDiscover={handleDiscoverMcpTools}
                            onDelete={handleDeleteMcpServer}
                            canDiscover={isEdit}
                            disabled={loading || saving}
                        />
                    ) : null}

                    <Box sx={{ display: 'flex', gap: 1, mt: 1, flexWrap: 'wrap' }}>
                        <Button
                            variant="outlined"
                            onClick={() => navigate(`/admin/clients/${encodeURIComponent(clientId)}/agents`)}
                            disabled={saving}
                            sx={{ textTransform: 'none', borderRadius: 3 }}
                        >
                            {t('adminAgents.cancel', 'Cancel')}
                        </Button>
                        <Button
                            variant="contained"
                            onClick={handleSave}
                            disabled={!canSave || loading || saving}
                            sx={{ textTransform: 'none', borderRadius: 3 }}
                        >
                            {saving ? t('adminAgents.saving', 'Saving…') : t('adminAgents.save', 'Save')}
                        </Button>
                    </Box>
                </Box>
            </Paper>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={4000}
                onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
                <Alert onClose={() => setSnackbar((s) => ({ ...s, open: false }))} severity={snackbar.severity} sx={{ width: '100%' }}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
}

export default AdminAgentForm;
